import { useCallback, useMemo } from 'react'

import { useNavigate, useParams } from '@tanstack/react-router'

import { usePromotionReport } from '@/api/hooks/usePromotionReport'
import { type RegionPromotionMilestoneDates } from '@/components/model/Region'
import { RegionPromotionView } from '@/components/templates/RegionPromotionView/RegionPromotionView'
import { downloadFromBase64 } from '@/lib/download'
import { collections } from '@/schema'
import { useRegionPromotionList } from '@/state/promotions/useRegionPromotionList'
import { useRegion } from '@/state/regions/useRegion'

import { useBackToPilote } from '../actions/useBackToPilote'

export const RegionPromotionPage = () => {
  const { initiativeId, regionId } = useParams({ from: '/promotions/$initiativeId/$regionId' })
  const { data: region } = useRegion({ regionId })
  const regionName = region?.name ?? ''
  const enabledPlacements = region?.promotionPlacements[initiativeId] ?? []
  const currentOperatingDate = region?.currentOperatingDate?.date
  const createEnabled = enabledPlacements.length > 0
  const { data: promotions } = useRegionPromotionList({ regionId, initiativeId })
  const navigate = useNavigate()

  const addPromotion = useCallback(async () => {
    await navigate({
      from: '/promotions/$initiativeId/$regionId',
      to: '/promotions/$initiativeId/$regionId/create',
      mask: { to: '/promotions/$initiativeId/$regionId', params: { initiativeId, regionId } },
    })
  }, [initiativeId, navigate, regionId])

  const openEditSheet = useCallback(
    async (promotionId: string) =>
      navigate({
        from: '/promotions/$initiativeId/$regionId',
        to: '/promotions/$initiativeId/$regionId/$promotionId',
        params: { promotionId },
        mask: { to: '/promotions/$initiativeId/$regionId', params: { initiativeId, regionId } },
      }),
    [initiativeId, navigate, regionId]
  )

  const regionPromotionMilestoneDates = useMemo(
    () =>
      Object.fromEntries(
        [...(region?.evenings ?? [])]
          .sort((a, b) => a.date.getTime() - b.date.getTime())
          .map((evening) => {
            const date = evening.date.toISOString().split('T')[0]
            return [date, region?.promotionMilestoneDates?.[initiativeId]?.[date] ?? null]
          }) ?? []
      ),
    [region?.evenings, region?.promotionMilestoneDates, initiativeId]
  )

  const onMilestoneFormSubmit = useCallback(
    async (data: RegionPromotionMilestoneDates) => {
      await collections.regions.update(regionId, { [`promotionMilestoneDates.${initiativeId}`]: data })
    },
    [regionId, initiativeId]
  )

  const { mutateAsync: getReport, isPending: isDownloadPending } = usePromotionReport()

  const onDownloadReport = useCallback(async () => {
    const base64Data = await getReport({ regionId })
    downloadFromBase64(
      base64Data,
      'rapport_promotions.xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    )
  }, [getReport, regionId])

  const onBackToPiloteClicked = useBackToPilote()

  return (
    <RegionPromotionView
      initiativeId={initiativeId}
      regionId={regionId}
      regionName={regionName}
      promotions={promotions}
      enabledPlacements={enabledPlacements}
      regionPromotionMilestoneDates={regionPromotionMilestoneDates}
      currentOperatingDate={currentOperatingDate}
      onBackToPiloteClicked={onBackToPiloteClicked}
      onAddPromotionClick={createEnabled ? addPromotion : undefined}
      onRowClick={openEditSheet}
      onMilestoneFormSubmit={onMilestoneFormSubmit}
      onDownloadReport={onDownloadReport}
      isDownloadPending={isDownloadPending}
    />
  )
}
